import {
  orderProcedureForDisplay,
  type Procedure,
  type ProcedureItem,
} from "@assetwatch/procedure-contract";
import type { ProcedureExecution } from "../../../queries/useProcedureExecution";
import type {
  ProcedureAnswer,
  ProcedureExecutionData,
  ProcedureExecutionItem,
  ProcedureExecutionSection,
} from "../../work-orders/ProcedureExecutionLayout";

const procedureItemTypeMap: Record<
  ProcedureItem["type"],
  ProcedureExecutionItem["type"]
> = {
  CHECKBOX: "checkbox",
  NUMERIC: "numeric",
  PHOTO: "photo",
  SINGLE_CHOICE: "single-choice",
  TEXT: "text",
};

function mapProcedureItem(item: ProcedureItem): ProcedureExecutionItem {
  return {
    description: item.description ?? undefined,
    id: item.id,
    label: item.label,
    options: "options" in item ? item.options : undefined,
    required: item.required,
    type: procedureItemTypeMap[item.type],
    unit: item.type === "NUMERIC" ? item.unit : undefined,
  };
}

export function mapProcedureExecution(
  procedure: Procedure,
  executionStatus: ProcedureExecution["status"] = "NOT_STARTED",
): ProcedureExecutionData {
  // The shared order is the one the Procedure edit view uses: sections without
  // a heading, each heading with its sections, then steps outside any section.
  const ordered = orderProcedureForDisplay(procedure);
  const sections: ProcedureExecutionSection[] = ordered.sections.map(
    ({ section, kind, headingId, ancestorIds, items }) => ({
      id: String(section.id),
      items: items.map(mapProcedureItem),
      label: section.title,
      ...(kind === "heading" ? { kind } : {}),
      ...(headingId === null ? {} : { headingId, ancestorIds }),
    }),
  );

  if (ordered.rootItems.length > 0) {
    sections.push({
      id: `${procedure.id}:root`,
      items: ordered.rootItems.map(mapProcedureItem),
      label: "Steps",
    });
  }
  return {
    heading: procedure.description ?? undefined,
    id: procedure.id,
    sections,
    status: (
      {
        NOT_STARTED: "not-started",
        IN_PROGRESS: "in-progress",
        COMPLETED: "completed",
      } as const
    )[executionStatus],
    title: procedure.title,
    versionLabel: `Version ${procedure.version}`,
  };
}

/**
 * The answers the server has saved, by item. PHOTO responses are left out:
 * photos are saved by the photo field as each upload finishes.
 */
export function executionAnswers(execution: ProcedureExecution | undefined) {
  return Object.fromEntries(
    (execution?.responses ?? []).flatMap((response) =>
      response.value.type === "PHOTO"
        ? []
        : [[response.itemId, response.value.value]],
    ),
  ) as Record<string, ProcedureAnswer>;
}

/** Server-saved photo ids per PHOTO item. */
export function savedPhotoAnswers(execution: ProcedureExecution | undefined) {
  return Object.fromEntries(
    (execution?.responses ?? []).flatMap((response) =>
      response.value.type === "PHOTO"
        ? [[response.itemId, response.value.fileIds]]
        : [],
    ),
  ) as Record<string, string[]>;
}
